<script lang="ts" setup>
// DataCompletudeBar (DEV-1650 / DEV-1661) — l'INDICATEUR de complétude de la
// période affichée, sur la page de données brutes d'une source.
//
// Décision de Mathieu du 08/09 : pas une bannière de plus. Une bande qui montre la
// période jour par jour, à la DATE du trou, avec la légende commune dessous, et le
// compte « n / m jours mesurés ». Rien n'est raconté : tout est peint.
//
// Ce composant ne fait AUCUNE requête : il reçoit la complétude déjà calculée à
// partir des lignes que la page a reçues pour ses indicateurs.
import { computed, getCurrentInstance, onBeforeUnmount, ref } from "vue"
import {
    ETATS_COMPLETUDE,
    etatsPresents,
    type Completude,
    type EtatCompletude,
} from "@modules/data/utils/completude"
import { dateFr } from "@modules/data/utils/fraicheurEnedis"
import DataCompletudeLegend from "./DataCompletudeLegend.vue"

const props = withDefaults(
    defineProps<{
        completude: Completude | null
        /** Titre court au dessus de la bande. */
        titre?: string
        /**
         * Passe les mots de la légende au texte courant (contraste AA). Faux
         * par défaut : la page de données générique, hors interrupteur, garde
         * le rendu qu'elle a toujours eu. Voir `DataCompletudeLegend`.
         */
        motsContrastes?: boolean
        /**
         * LA FENÊTRE REGARDÉE, en index de jours de `completude.jours`
         * (DEV-1508 lot B3). Bornes incluses.
         *
         * Additive : `null` (le défaut) = la bande peint le contexte entier,
         * exactement comme avant. Quand une fenêtre est donnée et qu'elle est
         * plus étroite que le contexte, les jours hors fenêtre s'estompent et
         * les deux jours de bord portent un liseré. La bande ne DÉCIDE de rien :
         * elle montre la fenêtre que l'appelant regarde, celle-là même que le
         * graphe pose sur son axe. Une seule fenêtre, deux représentations.
         */
        fenetre?: { debut: number; fin: number } | null
    }>(),
    { titre: "Complétude de la période", motsContrastes: false, fenetre: null }
)

/**
 * La bande n'annonce le geste (curseur de recadrage, texte non sélectionnable)
 * QUE si quelqu'un l'écoute. Un curseur `col-resize` sur une bande dont le
 * `brush` ne va nulle part promettrait une action qui n'existe pas : la page de
 * données générique monte cette bande sans écouter l'événement. Lu une fois au
 * montage, la présence d'un écouteur ne changeant pas en cours de vie.
 */
const recadrable = !!getCurrentInstance()?.vnode.props?.onBrush

/**
 * La fenêtre ramenée aux bornes réelles de la bande, ou `null` quand il n'y a
 * rien à marquer : pas de fenêtre, pas de jours, ou une fenêtre qui couvre le
 * contexte entier (rien ne serait estompé, et deux liserés aux deux bouts de la
 * bande diraient un resserrement qui n'a pas eu lieu).
 */
/** Le nombre de cases de la bande : une par jour du contexte. */
const nombreDeJours = computed<number>(
    () => props.completude?.jours.length ?? 0
)

/**
 * Une fenêtre ramenée aux bornes de la bande, bornes remises dans l'ordre, ou
 * `null` quand elle couvre le contexte entier (rien ne serait estompé, et deux
 * liserés aux deux bouts diraient un resserrement qui n'a pas eu lieu).
 */
function fenetreBornee(
    f: { debut: number; fin: number } | null
): { debut: number; fin: number } | null {
    const n = nombreDeJours.value
    if (!f || n === 0) return null
    const borne = (i: number) => Math.min(Math.max(Math.trunc(i), 0), n - 1)
    const a = borne(f.debut)
    const b = borne(f.fin)
    const [debut, fin] = a <= b ? [a, b] : [b, a]
    if (debut === 0 && fin === n - 1) return null
    return { debut, fin }
}

const fenetreMarquee = computed<{ debut: number; fin: number } | null>(() =>
    fenetreBornee(props.fenetre)
)

/** « 2026-09-01 » → « 01/09/2026 ». Pour le seul texte lu par une aide technique. */
function enDateLisible(jour: string): string {
    const [a, m, j] = jour.split("-")
    return a && m && j ? `${j}/${m}/${a}` : jour
}

/**
 * Le texte de la bande. Sans fenêtre resserrée, il est celui qu'il a toujours
 * été, au mot près. Avec, il dit AUSSI la fenêtre regardée : le liseré ne
 * remplace aucun texte, il le double.
 */
const texteDeLaBande = computed<string>(() => {
    const c = props.completude
    if (!c) return ""
    // ⚠️ « JOURS DUS », PAS « JOURS AFFICHÉS » DÈS QU'UN DÉLAI EST SERVI (lot
    // 6a, volet 2) : les jours plus récents que la promesse du flux sont
    // affichés mais pas encore dus, et ils ont quitté ce dénominateur. Sans
    // délai servi, `pasEncoreDus` vaut 0 et la phrase est celle d'avant, au
    // mot près.
    const base =
        c.pasEncoreDus > 0
            ? `${c.mesures} jours mesurés sur ${c.attendus} jours dus, et ${c.pasEncoreDus} jours pas encore dus`
            : `${c.mesures} jours mesurés sur ${c.attendus} jours affichés`
    const f = fenetreMarquee.value
    if (!f) return base
    const debut = c.jours[f.debut]
    const fin = c.jours[f.fin]
    if (!debut || !fin) return base
    return `${base}, fenêtre regardée du ${enDateLisible(debut.jour)} au ${enDateLisible(fin.jour)}`
})

const pourcentage = computed<number | null>(() => {
    if (!props.completude || props.completude.attendus === 0) return null
    return Math.round(
        (props.completude.mesures / props.completude.attendus) * 100
    )
})

/** N'afficher dans la légende que les états RÉELLEMENT présents sur la période. */
const etats = computed<EtatCompletude[]>(() =>
    props.completude ? etatsPresents(props.completude) : []
)

function descripteur(etat: EtatCompletude) {
    return ETATS_COMPLETUDE[etat]
}

/**
 * LES DEUX GESTES DE LA BANDE (DEV-1508 lot 2 puis lot 5e, écart T9, E6).
 *
 * On désigne des JOURS, pas des pixels : le composant ne décide de rien, il
 * remonte des index. Un appelant qui n'écoute pas se comporte exactement comme
 * avant — la page de données générique monte cette bande sans aucun écouteur.
 *
 *  • `brush` : la main se pose HORS de la fenêtre regardée, elle en choisit une
 *    nouvelle, des deux jours qu'elle a traversés ;
 *  • `deplacer` : la main se pose DANS la fenêtre regardée, elle la fait
 *    glisser SANS CHANGER SA LARGEUR, bornée par le contexte ;
 *  • `retour` : on revient au contexte — double-clic, ou clic simple à côté de
 *    la fenêtre regardée (S3 de la review du 23/09). Le geste est NOMMÉ dans la
 *    charge utile : l'appelant les distingue dans son suivi sans les deviner.
 */
const emit = defineEmits<{
    (e: "brush", bornes: { debut: number; fin: number }): void
    (e: "deplacer", bornes: { debut: number; fin: number }): void
    (e: "retour", geste: "clic" | "double_clic"): void
}>()

const bande = ref<HTMLElement | null>(null)

type Geste = {
    mode: "choisir" | "deplacer"
    /** La case où la main s'est posée. */
    depart: number
    /** La case sous la main, maintenant. */
    courant: number
    /** La fenêtre au moment où la main s'est posée (mode déplacer). */
    origine: { debut: number; fin: number } | null
}

const geste = ref<Geste | null>(null)

/**
 * La case sous l'abscisse donnée. Les jours se partagent la largeur de la bande
 * à parts égales (`flex: 1 1 0`), donc la case se lit par une règle de trois —
 * et jamais par le jour survolé, qu'un glisser hors de la bande ne donne plus.
 */
function indexSousLaSouris(clientX: number): number {
    const n = nombreDeJours.value
    const boite = bande.value?.getBoundingClientRect()
    if (!boite || boite.width <= 0 || n === 0) return 0
    const fraction = (clientX - boite.left) / boite.width
    return Math.min(n - 1, Math.max(0, Math.floor(fraction * n)))
}

/**
 * La fenêtre déplacée de `delta` cases, LARGEUR CONSERVÉE. Contre un bord du
 * contexte elle s'arrête au bord, sans se rétrécir : une fenêtre de 30 jours
 * poussée trop loin reste une fenêtre de 30 jours.
 */
function fenetreDeplacee(
    origine: { debut: number; fin: number },
    delta: number
): { debut: number; fin: number } {
    const n = nombreDeJours.value
    let debut = origine.debut + delta
    let fin = origine.fin + delta
    if (debut < 0) {
        fin -= debut
        debut = 0
    }
    if (fin > n - 1) {
        debut -= fin - (n - 1)
        fin = n - 1
    }
    return { debut: Math.max(0, debut), fin: Math.min(n - 1, fin) }
}

/**
 * LA FENÊTRE EN COURS DE GESTE, peinte pendant que la main travaille : l'œil
 * voit où il va avant de lâcher. Elle ne touche NI la prop `fenetre` (l'appelant
 * reste le seul à décider), NI le texte lu par une aide technique (un libellé
 * qui changerait à chaque pixel n'aiderait personne).
 */
const apercu = computed<{ debut: number; fin: number } | null>(() => {
    const g = geste.value
    if (!g) return null
    // TANT QUE LA MAIN N'A PAS GLISSÉ, RIEN N'EST PROPOSÉ (S3, review du
    // 23/09). Un simple clic peignait pendant l'appui une fenêtre d'un seul
    // jour sous le doigt, puis la reperdait au relâchement : la bande clignotait
    // vers un état que personne n'avait demandé et qui n'arrivait jamais.
    if (g.courant === g.depart) return null
    if (g.mode === "deplacer") {
        return g.origine
            ? fenetreBornee(fenetreDeplacee(g.origine, g.courant - g.depart))
            : null
    }
    return fenetreBornee({
        debut: Math.min(g.depart, g.courant),
        fin: Math.max(g.depart, g.courant),
    })
})

/** Ce que la bande dessine : le geste en cours s'il y en a un, la fenêtre sinon. */
const fenetreDessinee = computed<{ debut: number; fin: number } | null>(
    () => apercu.value ?? fenetreMarquee.value
)

/**
 * LES DEUX DATES DE LA FENÊTRE, ET OÙ LES POSER (S5, review du 23/09 : « on
 * peut zoomer sur une période mais le problème c'est qu'on voit pas les
 * dates »).
 *
 * Ce sont les dates RÉELLES des deux jours de bord, lues dans
 * `completude.jours` : aucune n'est calculée d'une largeur ni d'un pas. Les
 * positions sont celles des bords de la fenêtre dessinée, en pourcentage de la
 * bande — la même règle de trois que `indexSousLaSouris`, à l'envers, pour que
 * l'étiquette tombe sous le liseré qu'elle nomme.
 *
 * `null` quand rien n'est resserré : au contexte entier, les deux dates sont
 * déjà celles de la barre au dessus, et les écrire deux fois n'apprend rien.
 */
const datesDeLaFenetre = computed<{
    debut: { texte: string; position: string }
    fin: { texte: string; position: string }
} | null>(() => {
    const f = fenetreDessinee.value
    const jours = props.completude?.jours
    const n = nombreDeJours.value
    if (!f || !jours || n === 0) return null
    const debut = jours[f.debut]
    const fin = jours[f.fin]
    if (!debut || !fin) return null
    return {
        debut: {
            texte: enDateLisible(debut.jour),
            position: `${(f.debut / n) * 100}%`,
        },
        fin: {
            texte: enDateLisible(fin.jour),
            position: `${((f.fin + 1) / n) * 100}%`,
        },
    }
})

/** Vrai quand la main est posée sur la fenêtre et la fait glisser. */
const enDeplacement = computed(() => geste.value?.mode === "deplacer")

function ecouterLaMain(): void {
    window.addEventListener("mousemove", suivreLaMain)
    window.addEventListener("mouseup", finirLeGeste)
    window.addEventListener("keydown", surTouche)
}

function oublierLaMain(): void {
    window.removeEventListener("mousemove", suivreLaMain)
    window.removeEventListener("mouseup", finirLeGeste)
    window.removeEventListener("keydown", surTouche)
}

/**
 * VRAI QUAND LE CLIC QUI VIENT DE FINIR A DÉJÀ DEMANDÉ LE RETOUR AU CONTEXTE.
 *
 * Le premier clic d'un double-clic hors fenêtre passe maintenant par le retour
 * (S3) ; sans cette marque, le `dblclick` qui suit le redemanderait, et la page
 * rechargerait deux fois le même contexte pour un seul geste de la main.
 * Ce n'est pas un état d'affichage : il ne doit rien redessiner, donc pas de
 * `ref`.
 */
let retourDemandeParLeClic = false

function commencerLeGeste(evenement: MouseEvent): void {
    if (!recadrable || nombreDeJours.value === 0) return
    // Le SECOND clic d'un double-clic n'ouvre pas un geste : il appartient au
    // retour au contexte.
    if (evenement.detail >= 2) return
    retourDemandeParLeClic = false
    const index = indexSousLaSouris(evenement.clientX)
    const fenetre = fenetreMarquee.value
    const dansLaFenetre =
        !!fenetre && index >= fenetre.debut && index <= fenetre.fin
    geste.value = {
        mode: dansLaFenetre ? "deplacer" : "choisir",
        depart: index,
        courant: index,
        origine: fenetre,
    }
    // La sélection de texte du navigateur ne doit pas partir sous la main.
    evenement.preventDefault()
    ecouterLaMain()
}

function suivreLaMain(evenement: MouseEvent): void {
    const g = geste.value
    if (!g) return
    geste.value = { ...g, courant: indexSousLaSouris(evenement.clientX) }
}

/**
 * LE GESTE SE TERMINE OÙ QU'IL SOIT LÂCHÉ (écart T9) : l'écoute est posée sur
 * `window`, pas sur les cases. Un `mouseup` hors de la bande — au dessus du
 * graphe, dans la marge — terminait le geste dans le vide et la fenêtre ne
 * bougeait pas.
 */
function finirLeGeste(evenement: MouseEvent): void {
    const g = geste.value
    oublierLaMain()
    geste.value = null
    if (!g) return
    const courant = indexSousLaSouris(evenement.clientX)
    if (g.mode === "deplacer") {
        const delta = courant - g.depart
        if (delta === 0 || !g.origine) return
        emit("deplacer", fenetreDeplacee(g.origine, delta))
        return
    }
    // UN CLIC SANS GLISSER HORS DE LA FENÊTRE REVIENT AU CONTEXTE (S3, review
    // du 23/09 : « le reset fonctionne mal quand on clique à côté »).
    //
    // Il ne choisit toujours pas de fenêtre — une fenêtre d'un jour posée par
    // mégarde coûterait un chargement — mais il ne reste plus sans effet non
    // plus : cliquer à côté de ce qu'on regarde, c'est désigner tout le reste,
    // le même geste que le double-clic et que le bouton « Revenir au
    // contexte ». Sans fenêtre resserrée, il n'y a rien à défaire : inerte.
    if (courant === g.depart) {
        if (!fenetreMarquee.value) return
        retourDemandeParLeClic = true
        emit("retour", "clic")
        return
    }
    emit("brush", {
        debut: Math.min(g.depart, courant),
        fin: Math.max(g.depart, courant),
    })
}

/** Échap pendant un geste : la main se retire, la fenêtre ne bouge pas. */
function surTouche(evenement: KeyboardEvent): void {
    if (evenement.key !== "Escape" || !geste.value) return
    oublierLaMain()
    geste.value = null
    // Marque l'événement traité : l'écouteur global du retour au contexte
    // (DEV-1508 lot T10) ne doit pas AUSSI agir sur ce même Échap.
    evenement.preventDefault()
}

function surDoubleClic(): void {
    if (!recadrable) return
    oublierLaMain()
    geste.value = null
    // Le clic qui a ouvert ce double-clic l'a peut-être déjà demandé (S3) :
    // un seul retour par geste de la main.
    if (retourDemandeParLeClic) {
        retourDemandeParLeClic = false
        return
    }
    emit("retour", "double_clic")
}

onBeforeUnmount(oublierLaMain)

/**
 * L'infobulle d'un jour : sa date EN FRANÇAIS, son mot, et le détail servi.
 *
 * La date était servie en ISO (« 2026-07-21 · Enedis »), forme de machine sur
 * une surface client (lot 5a, écart D5). Le détail, lui, arrive déjà en clair de
 * `completude.ts` : ce composant ne traduit rien, il met en forme.
 */
function infobulle(jour: {
    jour: string
    etat: EtatCompletude
    detail: string | null
}): string {
    const mot = descripteur(jour.etat).mot
    const date = dateFr(jour.jour) ?? jour.jour
    return jour.detail
        ? `${date} · ${mot} · ${jour.detail}`
        : `${date} · ${mot}`
}
</script>

<template>
    <section
        v-if="completude"
        class="data-completude-bar"
        data-testid="completude"
    >
        <header class="data-completude-bar__entete">
            <span class="data-completude-bar__titre">{{ titre }}</span>
            <span class="data-completude-bar__compte">
                {{ completude.mesures }} / {{ completude.attendus }} jours
                mesurés
                <template v-if="pourcentage !== null">
                    ({{ pourcentage }} %)
                </template>
            </span>
        </header>

        <div
            ref="bande"
            class="data-completude-bar__bande"
            :class="{
                'data-completude-bar__bande--recadrable': recadrable,
                'data-completude-bar__bande--deplacement': enDeplacement,
            }"
            role="img"
            :aria-label="texteDeLaBande"
            @mousedown="commencerLeGeste"
            @dblclick="surDoubleClic"
        >
            <span
                v-for="(jour, index) in completude.jours"
                :key="jour.jour"
                class="data-completude-bar__jour"
                :class="{
                    'data-completude-bar__jour--hachures':
                        descripteur(jour.etat).motif === 'hachures',
                    'data-completude-bar__jour--hors-fenetre':
                        !!fenetreDessinee &&
                        (index < fenetreDessinee.debut ||
                            index > fenetreDessinee.fin),
                    'data-completude-bar__jour--dans-fenetre':
                        !!fenetreDessinee &&
                        index >= fenetreDessinee.debut &&
                        index <= fenetreDessinee.fin,
                    'data-completude-bar__jour--bord-gauche':
                        fenetreDessinee?.debut === index,
                    'data-completude-bar__jour--bord-droit':
                        fenetreDessinee?.fin === index,
                }"
                :style="{
                    '--jour-fond': descripteur(jour.etat).fond,
                    '--jour-trait': descripteur(jour.etat).trait,
                }"
                :title="infobulle(jour)"
            ></span>
        </div>

        <!-- LES DATES DE LA FENÊTRE, sous ses deux bords (S5). Elles doublent
             le liseré : celui-ci dit OÙ, elles disent QUAND. Le texte lu par
             une aide technique porte déjà les deux dates dans l'`aria-label`
             de la bande, d'où `aria-hidden` ici : rien ne serait annoncé deux
             fois, et rien ne changerait à chaque pixel du geste. -->
        <div
            v-if="datesDeLaFenetre"
            class="data-completude-bar__dates"
            aria-hidden="true"
        >
            <span
                class="data-completude-bar__date"
                data-testid="date-de-la-fenetre"
                :style="{ left: datesDeLaFenetre.debut.position }"
                >{{ datesDeLaFenetre.debut.texte }}</span
            >
            <span
                class="data-completude-bar__date data-completude-bar__date--fin"
                data-testid="date-de-la-fenetre"
                :style="{ left: datesDeLaFenetre.fin.position }"
                >{{ datesDeLaFenetre.fin.texte }}</span
            >
        </div>

        <DataCompletudeLegend
            :etats="etats"
            :par-etat="completude.parEtat"
            :mots-contrastes="motsContrastes"
        />
    </section>
</template>

<style scoped>
.data-completude-bar {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px 16px;
    /* Le bord de la bande, SANS repli inventé : `--p-content-border-color`
       vaut `{surface.200}` = #eeeeee avec le préréglage de l'application, et
       non le #e5e7eb qui était écrit ici. La bande et la barre de la fenêtre
       restent HORS du système de cartes : ce sont deux objets de contexte qui
       commandent les cartes en dessous, pas des contenus de même rang
       (item 38 du brief de design v1.1). */
    border: 1px solid var(--p-content-border-color);
    border-radius: 6px;
    background-color: var(--p-content-background, #fff);
}

.data-completude-bar__entete {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
}

.data-completude-bar__titre {
    font-size: 13px;
    font-weight: 600;
    color: var(--p-text-color, #2d2e32);
}

.data-completude-bar__compte {
    font-size: 12px;
    color: var(--p-text-muted-color, #64748b);
    font-variant-numeric: tabular-nums;
}

.data-completude-bar__bande {
    display: flex;
    align-items: stretch;
    gap: 1px;
    height: 18px;
    overflow: hidden;
    border-radius: 3px;
}

/* Le geste de recadrage, annoncé (DEV-1508 lot B3) : on désigne des jours, et
   la sélection de texte du navigateur ne doit pas partir sous la main qui
   glisse. Posé seulement quand un appelant écoute `brush`. */
.data-completude-bar__bande--recadrable {
    cursor: col-resize;
    user-select: none;
}

/* Les deux gestes s'annoncent avant d'être faits (écart T9) : sur la fenêtre
   regardée, la main SAISIT (`grab`) et la déplace sans changer sa largeur ;
   ailleurs sur la bande, elle CHOISIT une fenêtre (`col-resize`). */
.data-completude-bar__bande--recadrable
    .data-completude-bar__jour--dans-fenetre {
    cursor: grab;
}

/* Pendant le déplacement, la main tient la fenêtre — où que le pointeur se
   trouve au dessus de la bande. */
.data-completude-bar__bande--deplacement,
.data-completude-bar__bande--deplacement .data-completude-bar__jour {
    cursor: grabbing;
}

/* Les deux dates de la fenêtre (S5). Une piste de hauteur fixe, posée sous la
   bande : les étiquettes y sont placées en absolu sous les liserés, et la
   bande garde exactement la hauteur qu'elle avait. La date de fin se recule de
   sa propre largeur pour rester DANS la bande au bord droit. */
.data-completude-bar__dates {
    position: relative;
    height: 14px;
    margin-top: -2px;
}

.data-completude-bar__date {
    position: absolute;
    top: 0;
    font-size: 11px;
    line-height: 14px;
    white-space: nowrap;
    color: var(--p-text-color, #2d2e32);
    font-variant-numeric: tabular-nums;
}

.data-completude-bar__date--fin {
    transform: translateX(-100%);
}

.data-completude-bar__jour {
    flex: 1 1 0;
    min-width: 1px;
    background-color: var(--jour-fond);
}

/* LA FENÊTRE REGARDÉE (DEV-1508 lot B3). Hors de la fenêtre, un jour s'estompe
   sans changer de couleur ni de motif : il reste lisible, il reste à sa date,
   il n'est simplement plus celui que les graphes montrent. Rien n'est effacé,
   et surtout rien n'est repeint : un trou hors fenêtre reste un trou. */
.data-completude-bar__jour--hors-fenetre {
    opacity: 0.35;
}

/* Les deux bouts de la fenêtre, marqués à l'intérieur de la bande pour ne rien
   décaler. Le liseré prend la couleur primaire du thème, celle de l'onglet
   actif et de la sélection ; il double le texte, il ne le remplace pas. */
.data-completude-bar__jour--bord-gauche {
    box-shadow: inset 2px 0 0 var(--p-primary-color, #151c5d);
}

.data-completude-bar__jour--bord-droit {
    box-shadow: inset -2px 0 0 var(--p-primary-color, #151c5d);
}

/* Une fenêtre d'un seul jour porte les deux liserés. */
.data-completude-bar__jour--bord-gauche.data-completude-bar__jour--bord-droit {
    box-shadow:
        inset 2px 0 0 var(--p-primary-color, #151c5d),
        inset -2px 0 0 var(--p-primary-color, #151c5d);
}

/* Le motif porte l'absence : une case hachurée n'est pas une case vide, et elle
   n'est pas non plus un zéro. */
.data-completude-bar__jour--hachures {
    background-image: repeating-linear-gradient(
        45deg,
        var(--jour-trait) 0,
        var(--jour-trait) 1.5px,
        transparent 1.5px,
        transparent 4px
    );
}
</style>
